import React from 'react';

interface Order {
  _id: string;
  user?: { name: string; phone: string };
  items: Array<{ name: string; quantity: string; icon: string }>;
  deliveryAddress: {
    addressLine: string;
    city?: string;
    pincode?: string;
  };
  status: string;
  totalAmount: number;
  itemsTotal?: number;
  deliveryCharges?: number;
  deliveryDate: string;
  orderDate: string;
}

interface DeliveryHistoryProps {
  orders: Order[];
  onBack: () => void;
}

const DeliveryHistory: React.FC<DeliveryHistoryProps> = ({ orders, onBack }) => {
  const last7Days = orders.filter(order => {
    const deliveryDate = new Date(order.deliveryDate);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return deliveryDate >= sevenDaysAgo;
  });

  const totalEarnings = last7Days.reduce((sum, order) => sum + (order.deliveryCharges || 50), 0);
  const totalTips = last7Days.reduce((sum, order) => sum + ((order as any).tip || 0), 0);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>
          ← Back
        </button>
        <h1 style={styles.title}>Delivery History</h1>
      </div>

      <div style={styles.statsCard}>
        <div style={styles.statItem}>
          <h3 style={styles.statValue}>{last7Days.length}</h3>
          <p style={styles.statLabel}>Deliveries (Last 7 Days)</p>
        </div>
        <div style={styles.statItem}>
          <h3 style={{...styles.statValue, color: '#60b246'}}>₹{totalEarnings}</h3>
          <p style={styles.statLabel}>Total Earnings</p>
        </div>
        <div style={styles.statItem}>
          <h3 style={{...styles.statValue, color: '#28a745'}}>₹{totalTips}</h3>
          <p style={styles.statLabel}>Total Tips 💰</p>
        </div>
      </div>

      {last7Days.length === 0 ? (
        <div style={styles.emptyState}>
          <h2>📦 No deliveries yet</h2>
          <p>Your completed deliveries from the last 7 days will appear here</p>
        </div>
      ) : (
        <div style={styles.ordersList}>
          {last7Days.map((order) => (
            <div key={order._id} style={styles.orderCard}>
              <div style={styles.orderHeader}>
                <h3 style={styles.orderTitle}>Order #{order._id.slice(-6)}</h3>
                <span style={styles.badge}>✅ Delivered</span>
              </div>

              <div style={styles.dateInfo}>
                <p style={styles.dateText}>
                  📅 Ordered: {new Date(order.orderDate).toLocaleDateString('en-IN', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <p style={styles.dateText}>
                  ✅ Delivered: {new Date(order.deliveryDate).toLocaleDateString('en-IN', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>

              {order.user && (
                <div style={styles.section}>
                  <h4 style={styles.sectionTitle}>👤 Customer</h4>
                  <p style={styles.text}>{order.user.name}</p>
                </div>
              )}

              {order.deliveryAddress && (
                <div style={styles.section}>
                  <h4 style={styles.sectionTitle}>📍 Delivery Address</h4>
                  <p style={styles.address}>
                    {order.deliveryAddress.addressLine}
                    {order.deliveryAddress.city && `, ${order.deliveryAddress.city}`}
                    {order.deliveryAddress.pincode && ` - ${order.deliveryAddress.pincode}`}
                  </p>
                </div>
              )}

              {order.items && order.items.length > 0 && (
                <div style={styles.section}>
                  <h4 style={styles.sectionTitle}>🛒 Items ({order.items.length})</h4>
                  <div style={styles.itemsList}>
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div key={idx} style={styles.item}>
                        <span style={styles.itemIcon}>{item.icon}</span>
                        <span>{item.name} - {item.quantity}</span>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p style={styles.moreItems}>+{order.items.length - 3} more items</p>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.footer}>
                <div style={styles.earnings}>
                  <p style={{margin: 0, color: '#666', fontSize: '14px'}}>Your Earnings</p>
                  <p style={{margin: '5px 0 0', fontSize: '20px', fontWeight: 'bold', color: '#60b246'}}>
                    ₹{order.deliveryCharges || 50}
                  </p>
                  {(order as any).tip > 0 && (
                    <p style={{margin: '5px 0 0', fontSize: '16px', fontWeight: 'bold', color: '#28a745'}}>
                      💰 Tip: ₹{(order as any).tip}
                    </p>
                  )}
                </div>
                <div style={styles.totalAmount}>
                  <p style={{margin: 0, color: '#666', fontSize: '14px'}}>Order Total</p>
                  <p style={{margin: '5px 0 0', fontSize: '20px', fontWeight: 'bold'}}>
                    ₹{order.totalAmount}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: any = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
    paddingBottom: '40px',
  },
  header: {
    marginBottom: '20px',
  },
  backBtn: {
    padding: '10px 20px',
    background: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '15px',
    fontSize: '16px',
  },
  title: {
    fontSize: '28px',
    color: '#333',
    margin: 0,
  },
  statsCard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    marginBottom: '20px',
  },
  statItem: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#fc8019',
    margin: '0 0 5px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  ordersList: {
    display: 'grid',
    gap: '20px',
  },
  orderCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
  },
  orderTitle: {
    fontSize: '20px',
    color: '#333',
    margin: 0,
  },
  badge: {
    background: '#60b246',
    color: 'white',
    padding: '5px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  dateInfo: {
    background: '#f9f9f9',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  dateText: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#686b78',
  },
  section: {
    marginBottom: '15px',
  },
  sectionTitle: {
    fontSize: '16px',
    color: '#282c3f',
    marginBottom: '8px',
  },
  text: {
    margin: 0,
    color: '#686b78',
  },
  address: {
    margin: 0,
    color: '#686b78',
    lineHeight: '1.5',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    background: '#f9f9f9',
    borderRadius: '8px',
  },
  itemIcon: {
    fontSize: '24px',
  },
  moreItems: {
    margin: '5px 0 0',
    color: '#fc8019',
    fontWeight: '600',
    fontSize: '14px',
  },
  footer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    marginTop: '15px',
    paddingTop: '15px',
    borderTop: '2px solid #f0f0f0',
  },
  earnings: {
    textAlign: 'center',
    padding: '10px',
    background: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #60b246',
  },
  totalAmount: {
    textAlign: 'center',
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '8px',
  },
};

export default DeliveryHistory;
